import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { HttpService } from './http.service';
import { ApiEnvelope, unwrapApiData } from '../models/api-response.model';

const NAME_PREFIX_GENERIC_SLUG = 'name-prefix';

export interface NamePrefixOption {
  id: string;
  label: string;
  displayOrder?: number;
}

interface GenericValueApiRow {
  id?: string;
  value?: string;
  slug?: string;
  display_order?: number;
  displayOrder?: number;
  active?: boolean;
}

interface GenericDetailApi {
  values?: GenericValueApiRow[];
}

@Injectable({ providedIn: 'root' })
export class NamePrefixOptionsService {
  private readonly http = inject(HttpService);
  private cache$: Observable<NamePrefixOption[]> | null = null;
  private cachedOptions: NamePrefixOption[] = [];

  loadActiveOptions(): Observable<NamePrefixOption[]> {
    if (!this.cache$) {
      this.cache$ = this.http
        .get<ApiEnvelope<GenericDetailApi>>(
          `/v1/generics/by-slug/${encodeURIComponent(NAME_PREFIX_GENERIC_SLUG)}`,
        )
        .pipe(
          map((res) => this.mapActiveValues(unwrapApiData(res))),
          tap((options) => {
            this.cachedOptions = options;
          }),
          catchError(() => of([])),
          shareReplay(1),
        );
    }
    return this.cache$;
  }

  getCachedOptions(): NamePrefixOption[] {
    return this.cachedOptions;
  }

  resolveLabelById(namePrefixId: string | null | undefined): string {
    const id = String(namePrefixId ?? '').trim();
    if (!id) {
      return '';
    }
    const match = this.cachedOptions.find((option) => option.id === id);
    return match?.label?.trim() ?? '';
  }

  private mapActiveValues(detail: GenericDetailApi | null | undefined): NamePrefixOption[] {
    const values = detail?.values ?? [];
    return values
      .filter(row => row.active !== false && row.id && row.value)
      .map(row => ({
        id: String(row.id),
        label: String(row.value).trim(),
        displayOrder: Number(row.display_order ?? row.displayOrder ?? 0),
      }))
      .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
  }
}
